import * as THREE from 'three';
import { ROCK_SPEED, ROCK_LIFETIME, ROCK_DAMAGE, PLAYER_RADIUS } from './constants.js';
import * as Audio from './Audio.js';

/**
 * Rock projectile that travels in an arc.
 */
export class Projectile {
  constructor(scene, origin, direction, ownerId) {
    this.scene = scene;
    this.ownerId = ownerId;
    this.alive = true;
    this.lifetime = ROCK_LIFETIME;
    this.age = 0;
    this.damage = ROCK_DAMAGE;

    // Position & velocity — flat, fast throw at chest height
    this.position = origin.clone();
    this.velocity = direction.clone().normalize().multiplyScalar(ROCK_SPEED);
    this.velocity.y = 1.0; // Very slight upward arc — stays at player height
    this.prevPosition = this.position.clone(); // for sweep collision

    // Visual
    const geo = new THREE.IcosahedronGeometry(0.45, 1);
    const positions = geo.attributes.position;
    for (let i = 0; i < positions.count; i++) {
      const v = new THREE.Vector3(
        positions.getX(i),
        positions.getY(i),
        positions.getZ(i)
      );
      v.multiplyScalar(0.8 + Math.random() * 0.4);
      positions.setXYZ(i, v.x, v.y, v.z);
    }
    geo.computeVertexNormals();

    this.mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      color: 0x888888,
      roughness: 0.9,
      metalness: 0.1,
    }));
    this.mesh.castShadow = true;
    this.mesh.position.copy(this.position);
    scene.add(this.mesh);

    // Trail particles
    this.trail = [];
    this.trailTimer = 0;
  }

  update(dt) {
    if (!this.alive) return;

    this.age += dt;
    if (this.age > this.lifetime) {
      this.destroy();
      return;
    }

    // Save previous position for sweep collision
    this.prevPosition.copy(this.position);

    // Apply gravity (reduced — this is a thrown rock, not a cannonball)
    this.velocity.y -= 6 * dt;

    // Move
    this.position.addScaledVector(this.velocity, dt);

    // Ground collision
    if (this.position.y < 0.3) {
      this.createImpactEffect();
      Audio.rockImpact();
      this.destroy();
      return;
    }

    // Spin
    this.mesh.rotation.x += dt * 8;
    this.mesh.rotation.z += dt * 5;

    this.mesh.position.copy(this.position);

    // Trail effect
    this.trailTimer += dt;
    if (this.trailTimer > 0.05) {
      this.trailTimer = 0;
      this._addTrailParticle();
    }
    this._updateTrail(dt);
  }

  _addTrailParticle() {
    const geo = new THREE.SphereGeometry(0.15, 4, 4);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x999999,
      transparent: true,
      opacity: 0.5,
    });
    const p = new THREE.Mesh(geo, mat);
    p.position.copy(this.position);
    this.scene.add(p);
    this.trail.push({ mesh: p, life: 0.4 });
  }

  _updateTrail(dt) {
    for (let i = this.trail.length - 1; i >= 0; i--) {
      const t = this.trail[i];
      t.life -= dt;
      t.mesh.material.opacity = t.life;
      t.mesh.scale.multiplyScalar(0.97);
      if (t.life <= 0) {
        this.scene.remove(t.mesh);
        t.mesh.geometry.dispose();
        t.mesh.material.dispose();
        this.trail.splice(i, 1);
      }
    }
  }

  checkCollision(player) {
    if (!this.alive || player.id === this.ownerId || !player.alive) return false;

    // ── Cylinder hitbox: XZ radius check + Y range check ──
    // This is way more reliable than a single sphere distance.
    // Player is a cylinder from y=0 to y=3.2 (PLAYER_HEIGHT)
    const playerBaseY = player.position.y;
    const playerTopY = playerBaseY + 3.5; // slightly generous top
    const xzRadius = PLAYER_RADIUS + 1.0; // 2.0 total XZ hitbox

    // Helper: check a single point against the player cylinder
    const hitTest = (pos) => {
      // Y must be within player height range
      if (pos.y < playerBaseY - 0.5 || pos.y > playerTopY) return false;
      // XZ distance
      const dx = pos.x - player.position.x;
      const dz = pos.z - player.position.z;
      const xzDist = Math.sqrt(dx * dx + dz * dz);
      return xzDist < xzRadius;
    };

    // Check current position
    if (hitTest(this.position)) return true;

    // Sweep check — sample along movement path to catch fast projectiles
    const moveVec = new THREE.Vector3().subVectors(this.position, this.prevPosition);
    const moveLen = moveVec.length();
    if (moveLen < 0.1) return false;

    // Sample every 0.5 units along the movement vector
    const steps = Math.ceil(moveLen / 0.5);
    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      const samplePos = this.prevPosition.clone().lerp(this.position, t);
      if (hitTest(samplePos)) return true;
    }

    return false;
  }

  createImpactEffect() {
    // Dust burst
    for (let i = 0; i < 12; i++) {
      const geo = new THREE.SphereGeometry(0.2 + Math.random() * 0.2, 4, 4);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xd4a574,
        transparent: true,
        opacity: 0.7,
      });
      const p = new THREE.Mesh(geo, mat);
      p.position.copy(this.position);
      this.scene.add(p);
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 8,
        Math.random() * 5,
        (Math.random() - 0.5) * 8
      );
      this.trail.push({ mesh: p, life: 0.6, vel });
    }
  }

  destroy() {
    this.alive = false;
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    // Clean up remaining trail particles after a delay
    setTimeout(() => {
      for (const t of this.trail) {
        this.scene.remove(t.mesh);
        t.mesh.geometry.dispose();
        t.mesh.material.dispose();
      }
      this.trail = [];
    }, 1000);
  }
}
